import { ChevronDown, ChevronRight, CircleHelp, Code2, Folder, FoldVertical, Play, Plus, Search, UnfoldVertical } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  isScriptEntryDescendant,
  scriptIsAttached,
  type ScriptEntry,
  type ScriptFolder,
} from "@/document/model/documentScripts";
import { documentStore, editorScriptsStore, shouldShowEditorScriptsPane, uiStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import { workspaceStore } from "@/editor/workspace";
import { CURVA_SCRIPT_ENTRY_DRAG_TYPE, setActiveScriptEntryDrag } from "@/editor/shell/shellTransfer";
import { parseScriptDescription } from "@/scripting/scriptParams";
import { ScriptContextMenu } from "./ScriptContextMenu";

type DropPlacement = "before" | "after" | "inside";

function compareScriptEntryNames(left: ScriptEntry, right: ScriptEntry): number {
  return left.name.localeCompare(right.name, undefined, { numeric: true, sensitivity: "base" });
}

function sortedScriptEntries(entries: ScriptEntry[]): ScriptEntry[] {
  return [...entries].sort(compareScriptEntryNames);
}

function collectFolderIds(entries: ScriptEntry[]): string[] {
  const ids: string[] = [];
  for (const entry of entries) {
    if (entry.type === "folder") {
      ids.push(entry.id);
      ids.push(...collectFolderIds(entry.children));
    }
  }
  return ids;
}

function scriptMatchesQuery(entry: Extract<ScriptEntry, { type: "script" }>, query: string): boolean {
  if (entry.name.toLowerCase().includes(query)) {
    return true;
  }
  const description = parseScriptDescription(entry.source);
  if (description?.toLowerCase().includes(query)) {
    return true;
  }
  return entry.source.toLowerCase().includes(query);
}

/** Keep folders that match or contain matches; expand matching folder names to their full subtree. */
function filterScriptTree(entries: ScriptEntry[], rawQuery: string): ScriptEntry[] {
  const query = rawQuery.trim().toLowerCase();
  if (!query) {
    return sortedScriptEntries(entries);
  }

  const filter = (items: ScriptEntry[]): ScriptEntry[] => {
    const out: ScriptEntry[] = [];
    for (const entry of sortedScriptEntries(items)) {
      if (entry.type === "folder") {
        const folderMatches = entry.name.toLowerCase().includes(query);
        if (folderMatches) {
          out.push(entry);
          continue;
        }
        const children = filter(entry.children);
        if (children.length > 0) {
          const folder: ScriptFolder = { ...entry, children };
          out.push(folder);
        }
        continue;
      }
      if (scriptMatchesQuery(entry, query)) {
        out.push(entry);
      }
    }
    return out;
  };

  return filter(entries);
}

export interface ScriptMoveTarget {
  parentFolderId: string | undefined;
  index: number;
}

interface ScriptsPanelProps {
  allowScripts: boolean;
  onCreateScript: (parentFolderId?: string) => string | undefined;
  onCreateFolder: () => string | undefined;
  onRenameEntry: (entryId: string, name: string) => void;
  onDuplicateScript: (scriptId: string) => string | undefined;
  onDeleteEntry: (entryId: string) => void;
  onMoveEntry: (entryId: string, target: ScriptMoveTarget) => void;
  onRunScript: (scriptId: string) => void | Promise<void>;
  onRunEditorScript?: (scriptId: string) => void | Promise<void>;
  onCopyEditorScriptToDocument?: (entryId: string) => void;
}

export function ScriptsPanel({
  allowScripts,
  onCreateScript,
  onCreateFolder,
  onRenameEntry,
  onDuplicateScript,
  onDeleteEntry,
  onMoveEntry,
  onRunScript,
  onRunEditorScript,
  onCopyEditorScriptToDocument,
}: ScriptsPanelProps) {
  const document = useStoreSubscribe(documentStore.document);
  const editorLibrary = useStoreSubscribe(editorScriptsStore.library);
  const workspace = useStoreSubscribe(workspaceStore.state);
  const activeSession = workspace.sessions.find(session => session.id === workspace.activeSessionId);
  const editingEditorScripts = (activeSession?.kind ?? "document") === "editorScripts";
  const library = document.scripts;
  const [searchQuery, setSearchQuery] = useState("");
  const libraryItems = library?.items ?? [];
  const editorLibraryItems = editorLibrary?.items ?? [];
  const showEditorScripts = shouldShowEditorScriptsPane({
    editingEditorScripts,
    itemCount: editorLibraryItems.length,
  });
  const allFolderIds = useMemo(
    () => [...collectFolderIds(libraryItems), ...collectFolderIds(editorLibraryItems)],
    [document.revision, library, editorLibrary],
  );
  const rootItems = useMemo(
    () => filterScriptTree(libraryItems, searchQuery),
    [document.revision, library, searchQuery],
  );
  const editorRootItems = useMemo(
    () => filterScriptTree(editorLibraryItems, searchQuery),
    [editorLibrary, searchQuery],
  );
  const searching = searchQuery.trim().length > 0;
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [collapsedFolderIds, setCollapsedFolderIds] = useState<Set<string>>(() => new Set());
  const knownFolderIdsRef = useRef<Set<string>>(new Set());
  const [contextMenu, setContextMenu] = useState<{
    entryId: string;
    kind: "script" | "folder";
    mode: "document" | "editor";
    x: number;
    y: number;
  } | undefined>(undefined);
  const [runningId, setRunningId] = useState<string | undefined>(undefined);
  const [draggingId, setDraggingId] = useState<string | undefined>(undefined);
  const [draggingSource, setDraggingSource] = useState<"document" | "editor" | undefined>(undefined);
  const [dropIndicator, setDropIndicator] = useState<{ entryId: string; placement: DropPlacement } | undefined>(undefined);

  const editorRows = useMemo(
    () => flattenVisibleEntries(editorRootItems, searching ? new Set() : collapsedFolderIds),
    [collapsedFolderIds, editorRootItems, searching],
  );

  // New folders start collapsed (including the initial library load).
  useEffect(() => {
    const known = knownFolderIdsRef.current;
    const currentIds = new Set(allFolderIds);
    setCollapsedFolderIds(current => {
      const next = new Set(current);
      let changed = false;
      for (const id of allFolderIds) {
        if (!known.has(id)) {
          next.add(id);
          known.add(id);
          changed = true;
        }
      }
      for (const id of [...known]) {
        if (!currentIds.has(id)) {
          known.delete(id);
          if (next.delete(id)) {
            changed = true;
          }
        }
      }
      return changed ? next : current;
    });
  }, [allFolderIds]);

  if (!allowScripts) {
    return (
      <div className="flex min-h-0 flex-1 flex-col p-3 text-[12px] text-slate-500">
        Enable Scripts in the View menu to manage document scripts.
      </div>
    );
  }

  function toggleFolder(folderId: string): void {
    if (searching) {
      return;
    }
    setCollapsedFolderIds(current => {
      const next = new Set(current);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }

  function expandAllFolders(): void {
    setCollapsedFolderIds(new Set());
  }

  function collapseAllFolders(): void {
    setCollapsedFolderIds(new Set(allFolderIds));
  }

  function resolveDropTarget(overEntryId: string, placement: DropPlacement): ScriptMoveTarget | undefined {
    if (!library || !draggingId || draggingId === overEntryId) {
      return undefined;
    }
    if (isScriptEntryDescendant(library, draggingId, overEntryId)) {
      return undefined;
    }

    const overPath = findPath(library.items, overEntryId);
    if (!overPath) {
      return undefined;
    }

    if (placement === "inside") {
      if (overPath.entry.type !== "folder") {
        return undefined;
      }
      return { parentFolderId: overEntryId, index: overPath.entry.children.length };
    }

    const parentFolderId = overPath.parentFolderId;
    const index = placement === "before" ? overPath.index : overPath.index + 1;
    return { parentFolderId, index };
  }

  function handleDropOnEntry(overEntryId: string, placement: DropPlacement): void {
    if (!draggingId) {
      return;
    }
    if (draggingSource === "editor") {
      onCopyEditorScriptToDocument?.(draggingId);
      setDropIndicator(undefined);
      setDraggingId(undefined);
      setDraggingSource(undefined);
      return;
    }
    const target = resolveDropTarget(overEntryId, placement);
    setDropIndicator(undefined);
    setDraggingId(undefined);
    setDraggingSource(undefined);
    if (!target) {
      return;
    }
    onMoveEntry(draggingId, target);
  }

  const rows = flattenVisibleEntries(rootItems, searching ? new Set() : collapsedFolderIds);
  const libraryEmpty = (library?.items.length ?? 0) === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
        <span className="text-[11px] uppercase tracking-wide text-slate-500">
          {showEditorScripts ? "Document scripts" : "Scripts"}
        </span>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            className="flex h-6 items-center gap-1 rounded px-1.5 text-[11px] text-slate-300 hover:bg-white/[0.08] hover:text-slate-100"
            onClick={() => {
              const folderId = onCreateFolder();
              if (folderId) {
                setEditingId(folderId);
              }
            }}
          >
            <Folder className="size-3.5" aria-hidden="true" />
            Folder
          </button>
          <button
            type="button"
            className="flex h-6 items-center gap-1 rounded px-1.5 text-[11px] text-slate-300 hover:bg-white/[0.08] hover:text-slate-100"
            onClick={() => {
              const scriptId = onCreateScript();
              if (scriptId) {
                uiStore.openScriptEditor(scriptId);
              }
            }}
          >
            <Plus className="size-3.5" aria-hidden="true" />
            Script
          </button>
        </div>
      </div>
      <div className="shrink-0 border-b border-white/10 px-3 py-2">
        <div className="flex items-center gap-1.5">
          <label className="relative block min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-slate-500" aria-hidden="true" />
            <input
              type="search"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              placeholder="Search scripts"
              aria-label="Search scripts"
              className="h-7 w-full rounded-sm border border-white/10 bg-[#0f1318] py-1 pl-7 pr-2 text-[12px] text-slate-100 outline-none placeholder:text-slate-500 focus:border-sky-400/70"
            />
          </label>
          <button
            type="button"
            className="grid size-7 shrink-0 place-items-center rounded-sm text-slate-400 hover:bg-white/[0.08] hover:text-slate-100 disabled:opacity-40"
            title="Expand all folders"
            aria-label="Expand all folders"
            disabled={searching || allFolderIds.length === 0}
            onClick={expandAllFolders}
          >
            <UnfoldVertical className="size-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="grid size-7 shrink-0 place-items-center rounded-sm text-slate-400 hover:bg-white/[0.08] hover:text-slate-100 disabled:opacity-40"
            title="Collapse all folders"
            aria-label="Collapse all folders"
            disabled={searching || allFolderIds.length === 0}
            onClick={collapseAllFolders}
          >
            <FoldVertical className="size-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto p-1"
        onDragOver={event => {
          if (!draggingId) {
            return;
          }
          event.preventDefault();
          event.dataTransfer.dropEffect = draggingSource === "editor" ? "copy" : "move";
        }}
        onDrop={event => {
          event.preventDefault();
          if (!draggingId) {
            return;
          }
          if (draggingSource === "editor") {
            onCopyEditorScriptToDocument?.(draggingId);
          } else {
            onMoveEntry(draggingId, { parentFolderId: undefined, index: library?.items.length ?? 0 });
          }
          setDropIndicator(undefined);
          setDraggingId(undefined);
          setDraggingSource(undefined);
        }}
      >
        {rows.length === 0 ? (
          <div className="px-2 py-6 text-center text-[11px] text-slate-500">
            {libraryEmpty
              ? "No scripts yet. Create one to attach to frames."
              : searching
                ? "No matching scripts."
                : "No scripts yet. Create one to attach to frames."}
          </div>
        ) : (
          rows.map(row => (
            <ScriptTreeRow
              key={row.entry.id}
              entry={row.entry}
              depth={row.depth}
              attached={row.entry.type === "script" && scriptIsAttached(document, row.entry.id)}
              description={row.entry.type === "script" ? parseScriptDescription(row.entry.source) : undefined}
              editing={editingId === row.entry.id}
              running={runningId === row.entry.id}
              collapsed={!searching && row.entry.type === "folder" && collapsedFolderIds.has(row.entry.id)}
              dragging={draggingId === row.entry.id}
              dropPlacement={dropIndicator?.entryId === row.entry.id ? dropIndicator.placement : undefined}
              onToggleFolder={() => {
                if (row.entry.type === "folder") {
                  toggleFolder(row.entry.id);
                }
              }}
              onOpen={() => {
                if (row.entry.type === "script") {
                  uiStore.openScriptEditor(row.entry.id);
                }
              }}
              onBeginRename={() => setEditingId(row.entry.id)}
              onCommitRename={name => {
                onRenameEntry(row.entry.id, name);
                setEditingId(undefined);
              }}
              onCancelRename={() => setEditingId(undefined)}
              onRun={() => {
                if (row.entry.type !== "script") {
                  return;
                }
                setRunningId(row.entry.id);
                void Promise.resolve(onRunScript(row.entry.id)).finally(() => {
                  setRunningId(current => (current === row.entry.id ? undefined : current));
                });
              }}
              onContextMenu={event => {
                event.preventDefault();
                event.stopPropagation();
                setContextMenu({
                  entryId: row.entry.id,
                  kind: row.entry.type === "folder" ? "folder" : "script",
                  mode: "document",
                  x: event.clientX,
                  y: event.clientY,
                });
              }}
              onDragStart={event => {
                event.dataTransfer.setData(CURVA_SCRIPT_ENTRY_DRAG_TYPE, row.entry.id);
                // copyMove: reorder in the panel (move) or drop onto a canvas frame (copy/run).
                event.dataTransfer.effectAllowed = "copyMove";
                setActiveScriptEntryDrag({
                  entryId: row.entry.id,
                  kind: row.entry.type === "folder" ? "folder" : "script",
                  source: "document",
                });
                setDraggingId(row.entry.id);
                setDraggingSource("document");
              }}
              onDragEnd={() => {
                setActiveScriptEntryDrag(undefined);
                setDraggingId(undefined);
                setDraggingSource(undefined);
                setDropIndicator(undefined);
              }}
              onDragOver={event => {
                if (!draggingId) {
                  return;
                }
                event.preventDefault();
                event.stopPropagation();
                event.dataTransfer.dropEffect = draggingSource === "editor" ? "copy" : "move";
                const placement = dropPlacementForEvent(event, row.entry.type === "folder");
                setDropIndicator({ entryId: row.entry.id, placement });
              }}
              onDrop={event => {
                event.preventDefault();
                event.stopPropagation();
                const placement = dropIndicator?.entryId === row.entry.id
                  ? dropIndicator.placement
                  : dropPlacementForEvent(event, row.entry.type === "folder");
                handleDropOnEntry(row.entry.id, placement);
              }}
            />
          ))
        )}
      </div>
      {showEditorScripts ? (
        <div className="flex max-h-[45%] min-h-0 shrink-0 flex-col border-t border-white/10">
          <div className="shrink-0 px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500">
            Editor scripts
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {editorRows.length === 0 ? (
              <div className="px-2 py-4 text-center text-[11px] text-slate-500">
                {searching ? "No matching editor scripts." : "No editor scripts."}
              </div>
            ) : (
              editorRows.map(row => (
                <ScriptTreeRow
                  key={`editor-${row.entry.id}`}
                  entry={row.entry}
                  depth={row.depth}
                  attached={false}
                  description={row.entry.type === "script" ? parseScriptDescription(row.entry.source) : undefined}
                  editing={false}
                  running={runningId === row.entry.id}
                  collapsed={!searching && collapsedFolderIds.has(row.entry.id)}
                  dragging={draggingId === row.entry.id}
                  dropPlacement={undefined}
                  onToggleFolder={() => toggleFolder(row.entry.id)}
                  onOpen={() => {
                    if (row.entry.type === "script") {
                      /* Editor scripts are readonly — open is a no-op; use Run / Copy. */
                    }
                  }}
                  onBeginRename={() => undefined}
                  onCommitRename={() => undefined}
                  onCancelRename={() => undefined}
                  onRun={async () => {
                    if (row.entry.type !== "script" || !onRunEditorScript) {
                      return;
                    }
                    setRunningId(row.entry.id);
                    try {
                      await onRunEditorScript(row.entry.id);
                    } finally {
                      setRunningId(undefined);
                    }
                  }}
                  onContextMenu={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    setContextMenu({
                      entryId: row.entry.id,
                      kind: row.entry.type === "folder" ? "folder" : "script",
                      mode: "editor",
                      x: event.clientX,
                      y: event.clientY,
                    });
                  }}
                  onDragStart={event => {
                    event.dataTransfer.setData(CURVA_SCRIPT_ENTRY_DRAG_TYPE, row.entry.id);
                    event.dataTransfer.effectAllowed = "copy";
                    setActiveScriptEntryDrag({
                      entryId: row.entry.id,
                      kind: row.entry.type === "folder" ? "folder" : "script",
                      source: "editor",
                    });
                    setDraggingId(row.entry.id);
                    setDraggingSource("editor");
                  }}
                  onDragEnd={() => {
                    setActiveScriptEntryDrag(undefined);
                    setDraggingId(undefined);
                    setDraggingSource(undefined);
                    setDropIndicator(undefined);
                  }}
                  onDragOver={() => undefined}
                  onDrop={() => undefined}
                />
              ))
            )}
          </div>
        </div>
      ) : null}
      {contextMenu ? (
        <ScriptContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          kind={contextMenu.kind}
          mode={contextMenu.mode}
          onRename={contextMenu.mode === "document"
            ? () => {
                setEditingId(contextMenu.entryId);
                setContextMenu(undefined);
              }
            : undefined}
          onNewScript={contextMenu.mode === "document" && contextMenu.kind === "folder"
            ? () => {
                const scriptId = onCreateScript(contextMenu.entryId);
                setContextMenu(undefined);
                if (scriptId) {
                  setCollapsedFolderIds(current => {
                    const next = new Set(current);
                    next.delete(contextMenu.entryId);
                    return next;
                  });
                  uiStore.openScriptEditor(scriptId);
                }
              }
            : undefined}
          onDuplicate={contextMenu.mode === "document" && contextMenu.kind === "script"
            ? () => {
                const createdId = onDuplicateScript(contextMenu.entryId);
                setContextMenu(undefined);
                if (createdId) {
                  uiStore.openScriptEditor(createdId);
                }
              }
            : undefined}
          onDelete={contextMenu.mode === "document"
            ? () => {
                onDeleteEntry(contextMenu.entryId);
                if (uiStore.scriptEditorScriptId.getValue() === contextMenu.entryId) {
                  uiStore.closeScriptEditor();
                }
                setContextMenu(undefined);
              }
            : undefined}
          onRun={contextMenu.mode === "editor" && contextMenu.kind === "script"
            ? () => {
                void onRunEditorScript?.(contextMenu.entryId);
                setContextMenu(undefined);
              }
            : undefined}
          onCopyToDocument={contextMenu.mode === "editor"
            ? () => {
                onCopyEditorScriptToDocument?.(contextMenu.entryId);
                setContextMenu(undefined);
              }
            : undefined}
          onClose={() => setContextMenu(undefined)}
        />
      ) : null}
    </div>
  );
}

interface FlatRow {
  entry: ScriptEntry;
  depth: number;
}

function flattenVisibleEntries(entries: ScriptEntry[], collapsedFolderIds: Set<string>, depth = 0): FlatRow[] {
  const rows: FlatRow[] = [];
  for (const entry of sortedScriptEntries(entries)) {
    rows.push({ entry, depth });
    if (entry.type === "folder" && !collapsedFolderIds.has(entry.id)) {
      rows.push(...flattenVisibleEntries(entry.children, collapsedFolderIds, depth + 1));
    }
  }
  return rows;
}

function findPath(
  entries: ScriptEntry[],
  entryId: string,
  parentFolderId?: string,
): { entry: ScriptEntry; index: number; parentFolderId?: string; parent: ScriptEntry[] } | undefined {
  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index]!;
    if (entry.id === entryId) {
      return { entry, index, parentFolderId, parent: entries };
    }
    if (entry.type === "folder") {
      const nested = findPath(entry.children, entryId, entry.id);
      if (nested) {
        return nested;
      }
    }
  }
  return undefined;
}

function dropPlacementForEvent(event: ReactDragEvent<HTMLElement>, canDropInside: boolean): DropPlacement {
  const rect = event.currentTarget.getBoundingClientRect();
  const localY = event.clientY - rect.top;
  if (!canDropInside) {
    return localY < rect.height / 2 ? "before" : "after";
  }
  if (localY < rect.height * 0.28) {
    return "before";
  }
  if (localY > rect.height * 0.72) {
    return "after";
  }
  return "inside";
}

function ScriptTreeRow({
  entry,
  depth,
  attached,
  description,
  editing,
  running,
  collapsed,
  dragging,
  dropPlacement,
  onToggleFolder,
  onOpen,
  onBeginRename,
  onCommitRename,
  onCancelRename,
  onRun,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: {
  entry: ScriptEntry;
  depth: number;
  attached: boolean;
  description?: string;
  editing: boolean;
  running: boolean;
  collapsed: boolean;
  dragging: boolean;
  dropPlacement?: DropPlacement;
  onToggleFolder: () => void;
  onOpen: () => void;
  onBeginRename: () => void;
  onCommitRename: (name: string) => void;
  onCancelRename: () => void;
  onRun: () => void;
  onContextMenu: (event: ReactMouseEvent) => void;
  onDragStart: (event: ReactDragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onDragOver: (event: ReactDragEvent<HTMLDivElement>) => void;
  onDrop: (event: ReactDragEvent<HTMLDivElement>) => void;
}) {
  const isFolder = entry.type === "folder";

  return (
    <div
      className={`group relative flex h-8 items-center gap-1 rounded px-1.5 hover:bg-white/[0.06] ${
        dragging ? "opacity-40" : ""
      } ${dropPlacement === "inside" ? "bg-sky-500/15 ring-1 ring-inset ring-sky-400/40" : ""}`}
      style={{ paddingLeft: 6 + depth * 12 }}
      draggable={!editing}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onContextMenu={onContextMenu}
      onDoubleClick={event => {
        event.preventDefault();
        onBeginRename();
      }}
      onClick={() => {
        if (editing) {
          return;
        }
        if (isFolder) {
          onToggleFolder();
          return;
        }
        onOpen();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (editing) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (isFolder) {
            onToggleFolder();
          } else {
            onOpen();
          }
        }
      }}
    >
      {dropPlacement === "before" ? <DropLine position="top" /> : null}
      {dropPlacement === "after" ? <DropLine position="bottom" /> : null}
      {isFolder ? (
        <span className="grid size-3.5 shrink-0 place-items-center text-slate-500">
          {collapsed ? <ChevronRight className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </span>
      ) : (
        <span className="size-3.5 shrink-0" aria-hidden="true" />
      )}
      {isFolder ? (
        <Folder className="size-3.5 shrink-0 text-slate-500" aria-hidden="true" />
      ) : (
        <Code2 className="size-3.5 shrink-0 text-slate-500" aria-hidden="true" />
      )}
      {editing ? (
        <ScriptRenameInput name={entry.name} onCommit={onCommitRename} onCancel={onCancelRename} />
      ) : (
        <span className="min-w-0 flex-1 truncate text-[12px] text-slate-200">{entry.name}</span>
      )}
      {!editing && attached ? (
        <span className="shrink-0 rounded bg-sky-500/15 px-1 py-0.5 text-[9px] uppercase tracking-wide text-sky-200/90">
          Attached
        </span>
      ) : null}
      {!editing && description ? (
        <ScriptDescriptionHint description={description} />
      ) : null}
      {!editing && entry.type === "script" ? (
        <button
          type="button"
          className="grid size-6 shrink-0 place-items-center rounded text-slate-400 opacity-0 hover:bg-white/10 hover:text-slate-100 group-hover:opacity-100 group-focus-within:opacity-100"
          aria-label={`Run ${entry.name}`}
          title={`Run ${entry.name}`}
          disabled={running}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            onRun();
          }}
        >
          <Play className="size-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

function ScriptDescriptionHint({ description }: { description: string }) {
  const [anchor, setAnchor] = useState<{ left: number; top: number } | undefined>(undefined);

  useEffect(() => {
    if (!anchor) {
      return;
    }
    const hide = () => setAnchor(undefined);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [anchor]);

  return (
    <>
      <button
        type="button"
        className="grid size-6 shrink-0 place-items-center rounded text-slate-500 opacity-70 hover:bg-white/10 hover:text-slate-200 group-hover:opacity-100 group-focus-within:opacity-100"
        aria-label="Script description"
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onMouseEnter={event => {
          const rect = event.currentTarget.getBoundingClientRect();
          setAnchor({ left: rect.right, top: rect.top });
        }}
        onMouseLeave={() => setAnchor(undefined)}
        onFocus={event => {
          const rect = event.currentTarget.getBoundingClientRect();
          setAnchor({ left: rect.right, top: rect.top });
        }}
        onBlur={() => setAnchor(undefined)}
      >
        <CircleHelp className="size-3.5" aria-hidden="true" />
      </button>
      {anchor
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed z-[3000] w-[360px] max-w-[calc(100vw-24px)] -translate-x-full rounded-md border border-white/10 bg-[#1c2229] px-3 py-2.5 text-[11px] leading-relaxed text-slate-200 shadow-xl shadow-black/40"
              style={{ left: anchor.left - 4, top: anchor.top + 22 }}
            >
              <div className="whitespace-pre-wrap">{description}</div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function DropLine({ position }: { position: "top" | "bottom" }) {
  return (
    <span
      className={`pointer-events-none absolute left-2 right-2 z-10 h-px bg-sky-300 shadow-[0_0_0_1px_rgba(125,211,252,0.35)] ${
        position === "top" ? "top-0" : "bottom-0"
      }`}
      aria-hidden="true"
    />
  );
}

function ScriptRenameInput({
  name,
  onCommit,
  onCancel,
}: {
  name: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(name);
  const trimmedDraft = draft.trim();
  return (
    <input
      className="min-w-0 flex-1 rounded-sm border border-sky-400/70 bg-[#0f1318] px-1 py-0 text-[12px] text-slate-50 outline-none"
      value={draft}
      autoFocus
      onFocus={event => event.currentTarget.select()}
      onChange={event => setDraft(event.target.value)}
      onClick={event => event.stopPropagation()}
      onDoubleClick={event => event.stopPropagation()}
      onBlur={() => onCommit(trimmedDraft || name)}
      onKeyDown={event => {
        if (event.key === "Enter") {
          event.preventDefault();
          onCommit(trimmedDraft || name);
        } else if (event.key === "Escape") {
          event.preventDefault();
          onCancel();
        }
      }}
    />
  );
}
